import { CheckCircleOutline, DoNotDisturbOn } from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material"
import { FC, useState } from "react"
import { useTranslation } from "react-i18next"
import { Locker } from "shared/locker"
import { SharedAccessPatternSaEnum } from "shared/sharedAccessPatternsData"
import { useAppSelector } from "src/app/hooks"
import { selectMe } from "src/features/me/meSlice"
import {
  useGetThingsQuery,
  useUpdateThingsMutation,
} from "../features/things/thingsApi"
import {
  buildServicePatch,
  isOutOfService,
  LockerServiceState,
  readServiceState,
  summarizeServicePatch,
} from "./lockerServiceState"
import SaLockerServiceExplainer from "./SaLockerServiceExplainer"

/**
 * Operator service switch for one locker. Rules live in `lockerServiceState.ts`.
 *
 * Always expanded, unlike the migration override next to it: this is the control
 * an operator comes to the page FOR during an incident, and a collapsed panel
 * that has to be found first is the wrong shape for that. It is also cheap to
 * leave open — one switch, one reason field, and no way to express an incoherent
 * state.
 *
 * The reason is required in BOTH directions. Putting a unit back into service is
 * as consequential as taking it out (it is what re-exposes it to customers), and
 * an audit log with holes in it cannot answer "who turned this back on".
 */
export const SaLockerServiceToggle: FC<{ locker: Locker }> = ({ locker }) => {
  const { t } = useTranslation()
  const me = useAppSelector(selectMe)
  const actor = me?.user?.email || me?.user?.id

  const currentlyOut = isOutOfService(locker)
  const currentState = readServiceState(locker)
  const targetState: LockerServiceState = currentlyOut
    ? "inService"
    : "outOfService"

  const [reason, setReason] = useState("")
  const [reasonTouched, setReasonTouched] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [applied, setApplied] = useState(false)

  const [updateThings, { isLoading: isSaving }] = useUpdateThingsMutation()

  // `updateThings` declares no `invalidatesTags`, so nothing re-reads the Thing on
  // its own. Subscribing with the SAME args `SaLockerPage` uses means RTK Query
  // dedupes to one cache entry, and refetching here updates the locker the page
  // (and this panel) renders. Without it the switch snaps back to its old position
  // on a successful write — which reads as "it failed" and invites a second click.
  const { refetch } = useGetThingsQuery({
    params: {
      "ap.name": SharedAccessPatternSaEnum.SaGetLockers,
      "ap.ids[0]": locker.id,
    },
  })

  const reasonMissing = reason.trim().length === 0

  const onApply = async () => {
    try {
      setError(null)
      // Timestamped at call time, not render time: the operator can sit on the
      // confirmation dialog and the note must say when the write happened.
      const patch = buildServicePatch({
        locker,
        targetState,
        reason: reason.trim(),
        actor,
        now: new Date().toISOString(),
      })
      await updateThings({
        body: { things: [patch] },
        params: { tenantId: locker.tenantId },
      }).unwrap()
      setConfirmOpen(false)
      setApplied(true)
      setReason("")
      setReasonTouched(false)
      refetch()
    } catch (e: any) {
      setError(e?.data?.error || e?.message || String(e))
    }
  }

  const previewChanges = summarizeServicePatch(
    buildServicePatch({
      locker,
      targetState,
      reason: reason.trim(),
      actor,
      now: "preview",
    }),
    currentlyOut,
  )

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: currentlyOut ? "warning.main" : "divider",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        sx={{ p: 2.5 }}
      >
        <Stack direction="column" spacing={0.5} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {currentlyOut ? (
              <DoNotDisturbOn sx={{ fontSize: 17, color: "warning.main" }} />
            ) : (
              <CheckCircleOutline sx={{ fontSize: 17, color: "success.main" }} />
            )}
            <Chip
              size="small"
              variant="outlined"
              color={currentlyOut ? "warning" : "success"}
              label={t(`lockerService.state.${currentState}`)}
              sx={{ height: 20, fontSize: "0.68rem" }}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {t(`lockerService.headerCaption.${currentState}`)}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {t("lockerService.switchLabel")}
          </Typography>
          <Switch
            size="small"
            color="warning"
            checked={currentlyOut}
            // The switch OPENS the intent; the confirm dialog commits it. A live
            // toggle would let one mis-click change what customers can see.
            onChange={() => {
              setApplied(false)
              setError(null)
              setConfirmOpen(true)
            }}
            inputProps={{ "aria-label": t("lockerService.switchLabel") }}
          />
        </Stack>
      </Stack>

      <Divider />
      <Box sx={{ p: 2.5 }}>
        <SaLockerServiceExplainer />

        <TextField
          required
          fullWidth
          multiline
          minRows={2}
          size="small"
          sx={{ mt: 2 }}
          label={t("lockerService.reason")}
          placeholder={t(`lockerService.reasonPlaceholder.${targetState}`)}
          error={reasonTouched && reasonMissing}
          helperText={
            reasonTouched && reasonMissing
              ? t("lockerService.reasonRequired")
              : t("lockerService.reasonHelp")
          }
          value={reason}
          onBlur={() => setReasonTouched(true)}
          onChange={(e) => {
            setApplied(false)
            setReason(e.target.value)
          }}
        />

        {error && (
          <Alert severity="error" variant="outlined" sx={{ mt: 2, fontSize: "0.8rem" }}>
            {error}
          </Alert>
        )}
        {applied && !error && (
          <Alert severity="success" variant="outlined" sx={{ mt: 2, fontSize: "0.8rem" }}>
            {t(`lockerService.applied.${currentState}`)}
          </Alert>
        )}

        <Collapse in={!!locker.outOfServiceNote}>
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {t("lockerService.noteHistory")}
            </Typography>
            <Typography
              variant="caption"
              component="pre"
              sx={{
                m: 0,
                mt: 0.5,
                p: 1,
                maxHeight: 120,
                overflow: "auto",
                borderRadius: 1,
                bgcolor: "action.hover",
                fontFamily: "monospace",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                color: "text.secondary",
              }}
            >
              {locker.outOfServiceNote}
            </Typography>
          </Box>
        </Collapse>
      </Box>

      <Dialog data-ui="UI-SL2W-151"
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t(`lockerService.confirmTitle.${targetState}`)}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(`lockerService.confirmBody.${targetState}`)}
          </Typography>
          <Typography variant="caption" fontFamily="monospace" fontWeight={600}>
            {locker.id}
          </Typography>
          {previewChanges.map((c) => (
            <Typography
              key={c}
              variant="caption"
              display="block"
              fontFamily="monospace"
            >
              {c}
            </Typography>
          ))}
          {reasonMissing && (
            <Alert
              severity="warning"
              variant="outlined"
              sx={{ mt: 2, fontSize: "0.8rem" }}
            >
              {t("lockerService.reasonRequired")}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={isSaving}
            sx={{ textTransform: "none" }}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={onApply}
            disabled={isSaving || reasonMissing}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: "none" }}
          >
            {t(`lockerService.confirmAction.${targetState}`)}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

export default SaLockerServiceToggle
